
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { PanelLeftClose, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SIDEBAR_MIN_WIDTH = 360;
const SIDEBAR_MAX_WIDTH = 900;

interface SidebarPanelProps {
  sidebarExpanded: boolean;
  sidebarWidth: number;
  isDesktop: boolean;
  isResizingSidebar: boolean;
  mobileView: 'map' | 'list';
  railWidth: number;
  onSetResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
  onCloseSidebar: () => void;
  onSetMobileView: (view: 'map' | 'list') => void;
  onRefresh?: () => Promise<void> | void;
  children: React.ReactNode;
}

const SidebarPanel: React.FC<SidebarPanelProps> = ({
  sidebarExpanded,
  sidebarWidth,
  isDesktop,
  isResizingSidebar,
  mobileView,
  railWidth,
  onSetResizing,
  onWidthChange,
  onCloseSidebar,
  onSetMobileView,
  onRefresh,
  children,
}) => {
  const { t } = useTranslation();
  const isVisible = isDesktop ? sidebarExpanded : mobileView === 'list';
  const [refreshing, setRefreshing] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Sidebar resize drag handling
  useEffect(() => {
    if (!isResizingSidebar || !isDesktop) return;
    const onMove = (e: MouseEvent) => {
      const hardMax = Math.min(SIDEBAR_MAX_WIDTH, window.innerWidth - 320);
      const next = Math.max(SIDEBAR_MIN_WIDTH, Math.min(hardMax, e.clientX));
      onWidthChange(next);
    };
    const onUp = () => onSetResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDesktop, isResizingSidebar, onWidthChange, onSetResizing]);

  // Clamp width on window resize
  useEffect(() => {
    if (!isDesktop) return;
    const hardMax = Math.min(SIDEBAR_MAX_WIDTH, window.innerWidth - 320);
    onWidthChange(Math.max(SIDEBAR_MIN_WIDTH, Math.min(hardMax, sidebarWidth)));
  }, [isDesktop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull-to-refresh for scroll container
  const pullStartY = useRef<number | null>(null);
  const isPulling = useRef(false);

  const onScrollTouchStart = useCallback((e: React.TouchEvent) => {
    if (isDesktop || !onRefresh || refreshing) return;
    const scrollEl = scrollRef.current;
    if (scrollEl && scrollEl.scrollTop <= 0) {
      pullStartY.current = e.touches[0].clientY;
      isPulling.current = false;
    }
  }, [isDesktop, onRefresh, refreshing]);

  const onScrollTouchMove = useCallback((e: React.TouchEvent) => {
    if (pullStartY.current === null || isDesktop || !onRefresh || refreshing) return;
    const dy = e.touches[0].clientY - pullStartY.current;
    const scrollEl = scrollRef.current;
    if (dy > 0 && scrollEl && scrollEl.scrollTop <= 0) {
      isPulling.current = true;
      setPullProgress(Math.min(dy / 80, 1));
    } else {
      isPulling.current = false;
      setPullProgress(0);
    }
  }, [isDesktop, onRefresh, refreshing]);

  const onScrollTouchEnd = useCallback(async () => {
    if (!isPulling.current || !onRefresh) { pullStartY.current = null; setPullProgress(0); return; }
    pullStartY.current = null;
    if (pullProgress >= 1) {
      setRefreshing(true);
      try { await onRefresh(); } catch {}
      setRefreshing(false);
    }
    setPullProgress(0);
    isPulling.current = false;
  }, [onRefresh, pullProgress]);

  // Mobile swipe-down to dismiss
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ y: number; t: number } | null>(null);
  const dragOffset = useRef(0);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (isDesktop) return;
    touchStart.current = { y: e.touches[0].clientY, t: Date.now() };
    dragOffset.current = 0;
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
    }
  }, [isDesktop]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current || isDesktop) return;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (dy > 0) {
      dragOffset.current = dy;
      if (panelRef.current) {
        panelRef.current.style.transform = `translateY(${dy}px)`;
      }
    }
  }, [isDesktop]);

  const onTouchEnd = useCallback(() => {
    if (!touchStart.current || isDesktop) return;
    const dy = dragOffset.current;
    const elapsed = Date.now() - touchStart.current.t;
    const velocity = dy / Math.max(elapsed, 1);
    touchStart.current = null;

    if (panelRef.current) {
      panelRef.current.style.transition = '';
    }

    // Dismiss if dragged > 100px or fast flick (velocity > 0.5 px/ms)
    if (dy > 100 || velocity > 0.5) {
      onSetMobileView('map');
    }
    if (panelRef.current) {
      panelRef.current.style.transform = '';
    }
  }, [isDesktop, onSetMobileView]);

  if (!isVisible) return null;

  return (
    <div
      ref={panelRef}
      className={`
        absolute z-mapOverlay flex flex-col glass-strong transition-all duration-300 ease-in-out
        ${isDesktop
          ? 'inset-auto bottom-0 shadow-2xl'
          : 'left-0 right-0 bottom-0'
        }
      `}
      style={{
        top: isDesktop
          ? 'calc(88px + var(--safe-area-top, 0px))'
          : 'calc(60px + var(--safe-area-top, 0px))',
        ...(isDesktop ? {
          left: railWidth,
          width: Math.min(sidebarWidth, window.innerWidth * 0.55, SIDEBAR_MAX_WIDTH),
          minWidth: SIDEBAR_MIN_WIDTH,
        } : undefined),
      }}
    >
      {isDesktop && sidebarExpanded && (
        <>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSetResizing(true);
            }}
            className={`hidden sm:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-20 rounded-full border items-center justify-center z-[2200] shadow-xl transition-colors ${isResizingSidebar
              ? 'bg-emerald-500/20 border-emerald-500/60'
              : 'bg-[var(--bg-surface)] border-[var(--border-secondary)] hover:border-emerald-500/50'
              }`}
            title={t("auto.layout.sidebarpanel.title.dra_for_a_justere_bredde", "Dra for å justere bredde")}
            aria-label={t("auto.layout.sidebarpanel.aria-label.juster_sidepanelbredde", "Juster sidepanelbredde")}
          >
            <div className="w-[2px] h-10 bg-[var(--text-muted)] rounded-full" />
          </button>
          <button
            onClick={onCloseSidebar}
            className="hidden sm:flex absolute top-4 right-3 z-[2200] p-2 rounded-xl bg-[var(--bg-surface)] hover:bg-[var(--bg-input)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title={t("auto.layout.sidebarpanel.title.skjul_sidepanel", "Skjul sidepanel")}
            aria-label={t("auto.layout.sidebarpanel.aria-label.lukk_sidepanel", "Lukk sidepanel")}
          >
            <PanelLeftClose size={16} />
          </button>
        </>
      )}

      <div
        className="sm:hidden w-full flex justify-center pt-3 pb-1 flex-shrink-0 touch-none"
        onClick={() => onSetMobileView('map')}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="w-12 h-1.5 bg-[var(--bg-surface)] rounded-full" />
      </div>

      <div
        ref={scrollRef}
        className="flex-1 flex flex-col overflow-hidden"
        onTouchStart={onScrollTouchStart}
        onTouchMove={onScrollTouchMove}
        onTouchEnd={onScrollTouchEnd}
      >
        {(pullProgress > 0 || refreshing) && (
          <div className="flex items-center justify-center py-2 shrink-0 transition-all" style={{ height: refreshing ? 40 : pullProgress * 40 }}>
            <Loader2 size={16} className={`text-emerald-400 ${refreshing ? 'animate-spin' : ''}`} style={{ opacity: refreshing ? 1 : pullProgress, transform: `rotate(${pullProgress * 360}deg)` }} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH };
export default SidebarPanel;
